import {
  Array,
  Boolean,
  InstanceOf,
  Literal,
  Number,
  Partial,
  Record as RuntypeRecord,
  String,
  Union
} from "runtypes";
import { throttle } from "throttle-debounce";
const MIN_DELAY = 16;
const AceTimestamp = Union(
  InstanceOf(Date),
  String.withConstraint((s) => !isNaN(Date.parse(s)))
);
const EditorLocation = RuntypeRecord({
  row: Number,
  column: Number
});
const compareEditorLocations = (first, second) => first.column === second.column && first.row === second.row;
const Complete = RuntypeRecord({
  type: Literal("complete"),
  timestamp: AceTimestamp,
  focused: Boolean,
  value: String,
  selection: RuntypeRecord({
    start: EditorLocation,
    end: EditorLocation
  }),
  cursor: EditorLocation,
  scroll: RuntypeRecord({
    top: Number,
    left: Number
  })
});
const getComplete = (editor) => Complete.check({
  type: "complete",
  timestamp: /* @__PURE__ */ new Date(),
  focused: editor.isFocused(),
  value: editor.getValue(),
  selection: editor.selection.getRange(),
  cursor: editor.selection.getCursor(),
  scroll: {
    top: editor.renderer.getScrollTop(),
    left: editor.renderer.getScrollLeft()
  }
});
const applyComplete = (editor, complete, valueOnly = false, partial = false) => {
  if (editor.getValue() !== complete.value) {
    safeChangeValue(editor, complete.value);
  }
  if (valueOnly) {
    return;
  }
  const { row, column } = complete.cursor;
  editor.selection.moveCursorTo(row, column);
  const { start, end } = complete.selection;
  editor.selection.setSelectionRange({
    start: { row: start.row, column: start.column },
    end: { row: end.row, column: end.column }
  });
  if (partial) {
    return;
  }
  const { top, left } = complete.scroll;
  editor.renderer.scrollToY(top);
  editor.renderer.scrollToX(left);
};
const Delta = RuntypeRecord({
  type: Literal("delta"),
  timestamp: AceTimestamp,
  focused: Boolean,
  start: EditorLocation,
  end: EditorLocation,
  action: Union(Literal("insert"), Literal("remove")),
  lines: Array(String)
}).And(
  Partial({
    id: Number
  })
);
const applyDelta = (editor, delta) => editor.session.getDocument().applyDelta(delta);
const Selection = RuntypeRecord({
  start: EditorLocation,
  end: EditorLocation
});
const compareSelections = (first, second) => compareEditorLocations(first.start, second.start) && compareEditorLocations(first.end, second.end);
const selectionIsEmpty = (selection) => selection.start.column === selection.end.column && selection.end.row === selection.end.row;
const SelectionChange = RuntypeRecord({
  type: Literal("selectionchange"),
  timestamp: AceTimestamp,
  focused: Boolean,
  start: EditorLocation,
  end: EditorLocation
});
const applySelectionChange = (editor, selectionChange) => editor.selection.setSelectionRange(selectionChange);
const CursorChange = RuntypeRecord({
  type: Literal("cursorchange"),
  timestamp: AceTimestamp,
  focused: Boolean,
  location: EditorLocation
});
const applyCursorChange = (editor, cursorChange) => editor.selection.moveCursorTo(cursorChange.location.row, cursorChange.location.column);
const ScrollPosition = RuntypeRecord({
  top: Number,
  left: Number
});
const ScrollChange = RuntypeRecord({
  type: Literal("scrollchange"),
  timestamp: AceTimestamp,
  focused: Boolean,
  top: Number,
  left: Number
});
const applyScrollChange = (editor, scrollChange) => {
  editor.renderer.scrollToY(scrollChange.top);
  editor.renderer.scrollToX(scrollChange.left);
};
const ExternalChange = RuntypeRecord({
  type: Literal("external"),
  timestamp: AceTimestamp
});
const AceRecord = Union(Complete, Delta, SelectionChange, CursorChange, ScrollChange, ExternalChange);
const applyAceRecord = (editor, aceRecord, partial = true) => {
  if (Complete.guard(aceRecord)) {
    applyComplete(editor, aceRecord, false, partial);
  } else if (Delta.guard(aceRecord)) {
    applyDelta(editor, aceRecord);
    editor.renderer.scrollCursorIntoView(editor.selection.getCursor());
  } else if (SelectionChange.guard(aceRecord)) {
    applySelectionChange(editor, aceRecord);
    editor.renderer.scrollCursorIntoView(editor.selection.getCursor());
  } else if (CursorChange.guard(aceRecord)) {
    applyCursorChange(editor, aceRecord);
    editor.renderer.scrollCursorIntoView(editor.selection.getCursor());
  } else if (ScrollChange.guard(aceRecord)) {
    applyScrollChange(editor, aceRecord);
  }
};
const deserializeAceRecordTimestamp = (aceRecord) => {
  aceRecord.timestamp = new Date(aceRecord.timestamp);
  return aceRecord;
};
const stream = (editor, callback) => {
  let running = true;
  let lastValue = editor.getValue();
  const changeListener = (delta) => {
    if (!running) {
      return;
    }
    if (editor.getValue() === lastValue) {
      return;
    }
    lastValue = editor.getValue();
    callback(Delta.check({ type: "delta", timestamp: (/* @__PURE__ */ new Date()).toISOString(), focused: editor.isFocused(), ...delta }));
  };
  let lastSelection = Selection.check(editor.selection.getRange());
  const selectionListener = throttle(100, () => {
    if (!running) {
      return;
    }
    const selection = Selection.check(editor.selection.getRange());
    if (compareSelections(selection, lastSelection) || selectionIsEmpty(selection)) {
      lastSelection = selection;
      return;
    }
    lastSelection = selection;
    callback(
      SelectionChange.check({
        type: "selectionchange",
        timestamp: /* @__PURE__ */ new Date(),
        focused: editor.isFocused(),
        ...selection
      })
    );
  });
  let lastCursor = EditorLocation.check(editor.selection.getCursor());
  const cursorListener = throttle(100, () => {
    if (!running) {
      return;
    }
    const cursor = EditorLocation.check(editor.selection.getCursor());
    if (compareEditorLocations(cursor, lastCursor) || !selectionIsEmpty(editor.selection.getRange())) {
      lastCursor = cursor;
      return;
    }
    lastCursor = cursor;
    callback(
      CursorChange.check({
        type: "cursorchange",
        timestamp: /* @__PURE__ */ new Date(),
        focused: editor.isFocused(),
        location: cursor
      })
    );
  });
  let lastScroll = ScrollPosition.check({ top: editor.renderer.getScrollTop(), left: editor.renderer.getScrollLeft() });
  const scrollListener = throttle(100, () => {
    if (!running) {
      return;
    }
    const scroll = ScrollPosition.check({ top: editor.renderer.getScrollTop(), left: editor.renderer.getScrollLeft() });
    if (scroll.top === lastScroll.top && scroll.left === lastScroll.left) {
      return;
    }
    lastScroll = scroll;
    callback(
      ScrollChange.check({
        type: "scrollchange",
        timestamp: /* @__PURE__ */ new Date(),
        focused: editor.isFocused(),
        ...scroll
      })
    );
  });
  editor.session.addEventListener("change", changeListener);
  editor.addEventListener("changeSelection", selectionListener);
  editor.addEventListener("changeSelection", cursorListener);
  editor.session.addEventListener("changeScrollTop", scrollListener);
  let restartTimer;
  const pause = () => {
    clearTimeout(restartTimer);
    running = false;
  };
  const restart = () => {
    restartTimer = setTimeout(() => {
      running = true;
    }, 200);
  };
  const stop = () => {
    clearTimeout(restartTimer);
    editor.session.removeEventListener("change", changeListener);
    editor.removeEventListener("changeSelection", selectionListener);
    editor.removeEventListener("changeSelection", cursorListener);
    editor.session.removeEventListener("changeScrollTop", scrollListener);
  };
  return { stop, pause, restart };
};
const record = (editor, options = { interval: 1e3 }) => {
  const interval = options.interval || 1e3;
  const records = [getComplete(editor)];
  const { stop: cancel } = stream(editor, (record2) => records.push(record2));
  const timer = setInterval(() => records.push(getComplete(editor)), interval);
  const addExternalChange = (change) => {
    records.push(ExternalChange.check({ ...change, type: "external", timestamp: /* @__PURE__ */ new Date() }));
  };
  const stop = () => {
    clearInterval(timer);
    cancel();
    records.push(getComplete(editor));
    return records;
  };
  return { stop, addExternalChange };
};
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const replay = (editor, trace, options = { start: 0, seek: false, speed: 1 }) => {
  const replayer = {};
  const start = Number.check(options.start ?? 0);
  const seek = Boolean.check(!!options.seek);
  const speed = Number.check(options.speed ?? 1);
  let cancelled = false;
  replayer.promise = new Promise(async (resolve) => {
    const renderer = editor.renderer;
    const wasVisible = renderer.$cursorLayer.isVisible;
    renderer.$cursorLayer.isVisible = true;
    const wasBlinking = renderer.$cursorLayer.isBlinking;
    renderer.$cursorLayer.setBlinking(true);
    const previousOpacity = renderer.$cursorLayer.element.style.opacity;
    renderer.$cursorLayer.element.style.opacity = 1;
    const wasHighlightActiveLine = editor.getHighlightActiveLine();
    editor.setHighlightActiveLine(true);
    const traceStartTime = new Date(trace[0].timestamp).valueOf();
    let startIndex = 0;
    if (start > 0) {
      let i = 0;
      for (; i < trace.length; i++) {
        const record2 = trace[i];
        const currentTime = new Date(record2.timestamp).valueOf() - traceStartTime;
        if (currentTime < start) {
          if (Complete.guard(record2)) {
            startIndex = i;
          }
          if (ExternalChange.guard(record2) && options.seekReducer) {
            options.seekReducer(record2);
          }
        }
      }
    }
    options.seekCompleted && options.seekCompleted();
    if (seek) {
      applyAceRecord(editor, trace[startIndex]);
      return resolve();
    }
    const startTime = (/* @__PURE__ */ new Date()).valueOf();
    for (let i = startIndex; i < trace.length && !cancelled; i++) {
      const nowTime = ((/* @__PURE__ */ new Date()).valueOf() - startTime) * speed + start;
      const aceRecord = trace[i];
      if (ExternalChange.guard(aceRecord) && options.onExternalChange) {
        options.onExternalChange(aceRecord);
      } else {
        applyAceRecord(editor, aceRecord, true);
      }
      if (trace[i + 1]) {
        const nextTime = new Date(trace[i + 1].timestamp).valueOf() - traceStartTime;
        const delay = nextTime - nowTime;
        if (delay > MIN_DELAY) {
          await sleep(delay);
        }
      }
    }
    renderer.$cursorLayer.element.style.opacity = previousOpacity;
    renderer.$cursorLayer.setBlinking(wasBlinking);
    renderer.$cursorLayer.isVisible = wasVisible;
    editor.setHighlightActiveLine(wasHighlightActiveLine);
    resolve();
  });
  replayer.stop = () => {
    cancelled = true;
  };
  return replayer;
};
const safeChangeValue = (editor, value) => {
  const position = editor.session.selection.toJSON();
  editor.setValue(value);
  editor.session.selection.fromJSON(position);
};
export {
  AceRecord,
  AceTimestamp,
  Complete,
  CursorChange,
  Delta,
  EditorLocation,
  ExternalChange,
  ScrollChange,
  ScrollPosition,
  Selection,
  SelectionChange,
  applyAceRecord,
  applyComplete,
  applyCursorChange,
  applyDelta,
  applyScrollChange,
  applySelectionChange,
  compareEditorLocations,
  compareSelections,
  deserializeAceRecordTimestamp,
  getComplete,
  record,
  replay,
  safeChangeValue,
  selectionIsEmpty,
  stream
};
//# sourceMappingURL=index.js.map
